import { Pagination } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  GridColumnVisibilityModel,
  GridColDef,
  GridFilterPanel,
  GridFooterContainer,
  GridLinkOperator,
  GridPagination,
  GridRowIdGetter,
  GridRowParams,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppGridId, GridIdsToSave } from "../../models/app/AppGridModels";
import {
  addGridSetting,
  useAddAppGridSettingMutation,
} from "../../redux/reducer/features/App/AppGridSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DmsManufacturer } from "../../models/features/ShoppingPortal/DmsManufacturer";
import { GridFilterModel } from "@mui/x-data-grid-pro";
interface ServerAppGridProps {
  columns: GridColDef[];
  isLoading: boolean;
  data: any[];
  height?: any;
  onSelect?: any;
  selectedRows?: any;
  getRowId?: GridRowIdGetter<any>;
  setPaging: any;
  paging: any;
  totalRecords: number;
  hideFooter?: boolean;
  hidePaginator?: boolean;
  disableSelection?: boolean;
  keepNonExistentRowsSelected?: boolean | undefined;
  hideColumnFilter?: boolean;
  hideCheckBoxes?: boolean;
  hidePaginationText?: boolean;
  hidePageSize?: boolean;
  gridId?: AppGridId | undefined;
  isRowSelectable?: ((params: GridRowParams<any>) => boolean) | undefined;
  customToolbar?: boolean;
  switchLabel?: string;
  switchChecked?: boolean;
  onSwitchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  manufacturerOptions?: DmsManufacturer[];
  selectedManufacturers?: DmsManufacturer[];
  onManufacturerChange?: (selected: DmsManufacturer[]) => void;
}

const operatorsWithoutValue = new Set(["isEmpty", "isNotEmpty"]);

const isValidFilterItem = (item: any) => {
  if (!item.columnField || !item.operatorValue) {
    return false;
  }

  if (operatorsWithoutValue.has(item.operatorValue)) {
    return true;
  }

  return item.value !== undefined && item.value !== null && item.value !== "";
};

const CustomToolbar = ({
  switchLabel,
  switchChecked,
  onSwitchChange,
  manufacturerOptions,
  selectedManufacturers,
  onManufacturerChange,
}: {
  switchLabel: string;
  switchChecked: boolean;
  onSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  manufacturerOptions?: DmsManufacturer[];
  selectedManufacturers?: DmsManufacturer[];
  onManufacturerChange?: (selected: DmsManufacturer[]) => void;
}) => {
  const allOption: DmsManufacturer = {
    manufacturerId: "all",
    code: "",
    name: "Select All",
    isActive: true,
  };
  const allSelected =
    (manufacturerOptions?.length ?? 0) > 0 &&
    (selectedManufacturers?.length ?? 0) === (manufacturerOptions?.length ?? 0);

  return (
    <GridToolbarContainer>
      <Box display={"flex"} justifyContent={"space-between"} width="100%">
        <Box>
          <GridToolbarColumnsButton {...({} as any)} />
          <GridToolbarFilterButton {...({} as any)} />
          <GridToolbarDensitySelector {...({} as any)} />
          <GridToolbarExport />
        </Box>
        <Box display={"flex"} gap={3}>
          <Box width={300}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={[allOption, ...(manufacturerOptions || [])]}
              value={selectedManufacturers}
              onChange={(event, value) => {
                if (value.some((v) => v.manufacturerId === "all")) {
                  if (!allSelected) {
                    onManufacturerChange?.(manufacturerOptions || []);
                  } else {
                    onManufacturerChange?.([]);
                  }
                } else {
                  onManufacturerChange?.(value);
                }
              }}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
                    checkedIcon={<CheckBoxIcon fontSize="medium" />}
                    style={{ marginRight: 5, padding: 0 }}
                    checked={
                      option.manufacturerId === "all" ? allSelected : selected
                    }
                  />
                  {option.name}
                </li>
              )}
              style={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label="Manufacturers" size="small" />
              )}
              renderTags={() => null}
            />
          </Box>
          <FormControlLabel
            checked={switchChecked}
            control={
              <Switch
                name="jason"
                value={switchChecked}
                onChange={onSwitchChange}
                size="small"
              />
            }
            label={switchLabel}
          />
          <GridToolbarQuickFilter />
        </Box>
      </Box>
    </GridToolbarContainer>
  );
};

const CustomFilterPanel = (props: any) => {
  const apiRef = useGridApiContext();

  const handleResetFilters = () => {
    apiRef.current.setFilterModel({
      items: [],
      linkOperator: GridLinkOperator.And,
    });
  };

  return (
    <Box>
      <GridFilterPanel {...props} />
      <Box
        sx={{
          position: "absolute",
          bottom: 8,
          right: 16,
          zIndex: 1,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={handleResetFilters}
        >
          Reset Filters
        </Button>
      </Box>
    </Box>
  );
};

const ServerAppGridPro: React.FunctionComponent<ServerAppGridProps> = (
  props,
) => {
  const { userId } = useSelector((state: AppState) => state.authReducer);
  const dispatch = useDispatch();
  const [saveSettingMutation, settingMutationResult] =
    useAddAppGridSettingMutation();

  const handleSetVisibilityModel = (model: GridColumnVisibilityModel) => {
    if (props.gridId !== undefined) {
      dispatch(
        addGridSetting({
          setting: { gridId: props.gridId, columnVisibilityModel: model },
        }),
      );
      if (GridIdsToSave.includes(props.gridId)) {
        var data = JSON.stringify(model);
        saveSettingMutation({
          data: { userId: userId ?? "", gridId: props.gridId, setting: data },
        });
      }
    }
  };
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: props.paging.items || [],
    quickFilterValues: props.paging.quickFilterValues || [],
    linkOperator:
      props.paging.logicalOperator === "or"
        ? GridLinkOperator.Or
        : GridLinkOperator.And,
  });

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <>
        <label></label>
        <Pagination
          color="primary"
          count={pageCount}
          page={page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </>
    );
  }
  function CustomFooter() {
    return (
      <GridFooterContainer sx={{ width: props.hidePaginator ? "auto" : "80%" }}>
        {props.hidePaginator ? <></> : <CustomPagination />}
        <GridPagination {...({} as any)} />
      </GridFooterContainer>
    );
  }
  const handleGetColumns = (): GridColDef[] => {
    return props.columns.map((col) => ({
      ...col,
      sortable: col.sortable !== false, // Enable sorting by default unless explicitly disabled
    }));
  };

  return (
    <>
      <Card>
        <Box sx={{ height: props.height ?? 620, width: "100%" }}>
          <DataGridPro
            className={`${
              props.hidePaginationText ? "hide-pagination-text" : " "
            }`}
            getRowId={props.getRowId}
            rows={props.data}
            columns={handleGetColumns()}
            rowsPerPageOptions={
              props.hidePageSize
                ? [10, 20, 50, 100, 500, 1000]
                : [10, 20, 50, 100]
            }
            pagination
            paginationMode="server"
            onPageChange={(newPage) => {
              props.setPaging({
                ...props.paging,
                pageIndex: newPage,
              });
            }}
            onPageSizeChange={(newPageSize) => {
              props.setPaging({
                ...props.paging,
                pageSize: newPageSize,
                pageIndex: 0,
              });
            }}
            sortingMode="server"
            disableMultipleColumnsSorting
            sortModel={props.paging.sorting ? [props.paging.sorting] : []}
            filterMode="server"
            page={props.paging.pageIndex}
            selectionModel={props.selectedRows}
            pageSize={props.hidePageSize ? undefined : props.paging.pageSize}
            hideFooter={props.hideFooter ? true : false}
            filterModel={filterModel}
            disableMultipleColumnsFiltering={false}
            onFilterModelChange={(model) => {
              setFilterModel(model);
              const validItems = model.items.filter((item) =>
                isValidFilterItem(item),
              );

              const hasQuickFilterValues =
                model.quickFilterValues &&
                model.quickFilterValues.length > 0 &&
                model.quickFilterValues.some((val) => val.trim() !== "");

              const previousQuickFilterValues =
                props.paging.quickFilterValues &&
                props.paging.quickFilterValues.length > 0 &&
                props.paging.quickFilterValues.some(
                  (val: string) => val.trim() !== "",
                );

              const newPaging = {
                ...props.paging,
                pageIndex: 0,
                items: validItems.map((n, index) => ({
                  id: n.id ?? index,
                  columnField: n.columnField,
                  operatorValue: n.operatorValue ?? "",
                  value: n.value,
                })),
                quickFilterValues: model.quickFilterValues ?? [],
                logicalOperator: model.linkOperator || "and",
              };
              const hasActualFilters =
                validItems.length > 0 || hasQuickFilterValues;
              const hadPreviousFilters =
                (props.paging.items && props.paging.items.length > 0) ||
                previousQuickFilterValues;

              if (
                hasActualFilters ||
                (hadPreviousFilters && !hasActualFilters)
              ) {
                props.setPaging(newPaging);
              }
            }}
            onSortModelChange={(model) => {
              props.setPaging({
                ...props.paging,
                pageIndex: 0,
                sorting: model.length > 0 ? model[0] : null,
              });
            }}
            rowCount={props.totalRecords}
            checkboxSelection={props.hideCheckBoxes ? false : true}
            disableSelectionOnClick
            loading={props.isLoading}
            onSelectionModelChange={(newSelection: any, detail: any) => {
              props.onSelect && props.onSelect(newSelection);
            }}
            localeText={{
              footerRowSelected: (count) =>
                count !== 1
                  ? `${count.toLocaleString()} rows selected`
                  : `${count.toLocaleString()} row selected`,
            }}
            components={{
              Toolbar: props.customToolbar ? CustomToolbar : GridToolbar,
              Pagination: CustomFooter,
              FilterPanel: CustomFilterPanel,
            }}
            componentsProps={{
              toolbar: props.customToolbar
                ? {
                    switchLabel: props.switchLabel,
                    switchChecked: props.switchChecked,
                    onSwitchChange: props.onSwitchChange,
                    manufacturerOptions: props.manufacturerOptions,
                    selectedManufacturers: props.selectedManufacturers,
                    onManufacturerChange: props.onManufacturerChange,
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  }
                : {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
            }}
            isRowSelectable={props.isRowSelectable}
            keepNonExistentRowsSelected={
              props.keepNonExistentRowsSelected ?? true
            }
            disableColumnFilter={props.hideColumnFilter}
            onColumnVisibilityModelChange={handleSetVisibilityModel}
          />
        </Box>
      </Card>
    </>
  );
};

export default ServerAppGridPro;
